import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppShellHeader, AppShellSidebar } from "@musa.tech/design-system";
import { useAuthContext } from "@/components/AuthProvider";
import { useAppDispatch } from "@/store/hooks";
import {
  fetchEstablishmentsForUser,
  fetchMaterialsForUser,
  clearEstablishmentAccess,
} from "@/store/establishmentAccessSlice";
import { CSATPopup } from "@/components/csat/CSATPopup";
import { useCSATPopupTrigger } from "@/hooks/useCSATPopupTrigger";
import { useCloudHumansBubble } from "@/hooks/useCloudHumansBubble";
import { useSidebarNavigation } from "@/hooks/useSidebarNavigation";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { DetailBreadcrumbProvider } from "@/contexts/DetailBreadcrumbContext";
import { useHeaderMenuItems } from "@/hooks/useHeaderMenuItems";
import { useRouteAccessDenied } from "@/components/RouteAccessContext";
import { HeaderChatButton } from "@/components/layout/HeaderChatButton";
import { HeaderMenuIcon } from "@/components/layout/HeaderMenuIcon";
import { isItemActive, navigationGroups } from "@/components/layout/navigation";
import { useAnalytics } from "@/hooks/useAnalytics";
import { isEmbedMode } from "@/embed/host";
import { useEmbedBridge } from "@/embed/useEmbedBridge";
import logoSymbol from "@/assets/logo-musa-symbol.svg";

/**
 * Shell das rotas autenticadas. É montado como rota de layout (um único
 * mount por sessão), então o menu, o header e a carga inicial do Redux não
 * são recriados a cada navegação.
 */
export const Layout = () => (
  <DetailBreadcrumbProvider>
    <LayoutContent />
  </DetailBreadcrumbProvider>
);

/**
 * Precisa estar dentro do `DetailBreadcrumbProvider`: `useBreadcrumb` lê o rótulo dinâmico do
 * contexto, e o `Outlet` (onde a página de detalhe publica esse rótulo) é renderizado aqui dentro.
 */
const LayoutContent = () => {
  const { profile, isAuthenticated } = useAuthContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { groups, isLoading: isNavigationLoading } = useSidebarNavigation();
  const { trackButtonClick } = useAnalytics();

  /** GR-712: um evento por item, nome vem de navigation.ts (fonte única). */
  const handleSelectItem = (id: string) => {
    const item = navigationGroups
      .flatMap((group) => group.items)
      .find((navItem) => navItem.id === id);
    if (item) {
      trackButtonClick({ buttonLabel: item.analyticsLabel });
    }
    navigate(id);
  };
  const breadcrumb = useBreadcrumb();
  const headerMenuItems = useHeaderMenuItems();
  const { isOpen: csatPopupOpen, handleClose: handleCSATClose } =
    useCSATPopupTrigger();
  const accessDenied = useRouteAccessDenied();
  useCloudHumansBubble();

  // Embutido em outro portal: menu e header são renderizados por ele, com o
  // que esta ponte manda. Dois shells empilhados seriam duas navegações
  // concorrentes na mesma tela.
  const isEmbedded = isEmbedMode();
  useEmbedBridge({ groups, isNavigationLoading });

  // Carregar estabelecimentos, geradores, estados e materiais (API) no Redux para filtros do portal
  useEffect(() => {
    if (!isAuthenticated || !profile) {
      dispatch(clearEstablishmentAccess());
      return;
    }
    dispatch(fetchEstablishmentsForUser());
    dispatch(fetchMaterialsForUser());
  }, [isAuthenticated, profile?.user_id, dispatch]);

  if (!isAuthenticated) {
    return <Outlet />;
  }

  // Embutido: sai a sidebar, FICA o header. Ele carrega ajuda, notificações,
  // conta, usuários e modo demo — coisas que mudam com frequência e que
  // ninguém quer reimplementar do lado de fora só para elas ficarem velhas lá.
  // Quem embute esconde o header próprio enquanto esta tela está visível, para
  // não empilhar dois.
  if (isEmbedded) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background">
        {/*
          Sem `actions`: o botão de chat depende do widget do CloudHumans, e
          ele não é montado no embed — quebrava dentro do iframe
          ("Cannot read properties of null (reading 'classList')"). Um botão
          permanentemente desabilitado seria pior que nenhum.

          A Central de Ajuda continua aqui: ela vive dentro do menu desde o
          MC-109, e `menuItems` é o mesmo dos dois lados.
        */}
        <AppShellHeader
          className="sticky top-0 z-20"
          breadcrumb={breadcrumb}
          onNavigate={navigate}
          menuIcon={HeaderMenuIcon}
          menuItems={headerMenuItems}
        />

        <main className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
          <Outlet />
        </main>

        <CSATPopup isOpen={csatPopupOpen} onClose={handleCSATClose} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full bg-background">
      <AppShellSidebar
        title="Portal do Gerador"
        logo={<img src={logoSymbol} alt="Musa" />}
        groups={groups}
        selectedId={location.pathname}
        isActiveItem={isItemActive}
        onSelectItem={handleSelectItem}
        isLoading={isNavigationLoading}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <AppShellHeader
          className="sticky top-0 z-20"
          breadcrumb={breadcrumb}
          onNavigate={navigate}
          actions={<HeaderChatButton />}
          menuIcon={HeaderMenuIcon}
          menuItems={headerMenuItems}
        />

        <main className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Não perguntar o que a pessoa achou de uma tela que ela não abriu. */}
      <CSATPopup
        isOpen={csatPopupOpen && !accessDenied}
        onClose={handleCSATClose}
      />
    </div>
  );
};
